import { useMsal } from '@azure/msal-react';
import {
  faCircleQuestion,
  faCloudArrowDown,
  faCog,
  faDashboard,
  faEllipsis,
  faExternalLink,
  faFileContract,
  faGrid,
  faLock,
  faMagnifyingGlassChart,
  faShip,
  faShoppingCart,
  faSitemap,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import logoBlack from '../../assets/logo/Humphree Trim and Stabilization black.png';
import logoWhite from '../../assets/logo/Humphree Trim and Stabilization white.png';
import { Config } from '../../config';
import { AppContext } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Logout } from '../../services/AuthService';
import PermissionGate, { ROLES } from '../../utils/PermissionGate';
import { ToggleButton } from '../../utils/ToggleButton';
import SidebarButton from './SidebarButton';
import { SidebarOemDropdown } from './SidebarOemDropdown';
import SidebarOemDropdownIndirect from './SidebarOemDropdownIndirect';
interface Props {
  showMenu: boolean;
  setShowMenu: (value: boolean) => void;
}

function Sidebar(props: Props) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();

  const { isHumphreeUser, currentOem, currentOemTenantId, setCurrentOemTenantId, representingOem } =
    useContext(AppContext);
  const location = useLocation();
  const { accounts } = useMsal();
  const account = accounts[0];
  const [openUserEdit, setOpenUserEdit] = useState(false);
  const oems = useSelector((state: any) => state.oems.data);
  const ref = useDetectClickOutside({
    onTriggered: () => {
      setOpenUserEdit(false);
    },
  });

  function toggleMenuOnlyMobile() {
    if (window.innerWidth < 768) {
      props.setShowMenu(!props.showMenu);
    }
  }

  useEffect(() => {
    const breakpoint = 768; // tailwind's "md" breakpoint
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);

    const handleMediaChange = (e: any) => {
      if (e.matches) {
        props.setShowMenu(true);
      } else {
        props.setShowMenu(false);
      }
    };

    handleMediaChange(mediaQuery);

    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  return (
    <>
      <div
        onClick={(e) => {
          props.setShowMenu(!props.showMenu);
        }}
        className={`absolute z-30 transition duration-500 md:hidden ${
          props.showMenu ? 'opacity-80 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }
        } w-screen h-screen bg-black`}
      ></div>
      <div
        ref={ref}
        className={`transition absolute z-40 -translate-x-72 ${
          props.showMenu ? 'flex opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } w-full sm:w-60 md:static flex-col flex-shrink-0 items-start justify-between 2xl:w-80 h-full bg-gray-5 p-5 py-11`}
      >
        <div className='flex flex-col w-full'>
          <div className='opacity-0 max-w-40 max-h-[60px] md:opacity-100 flex self-center mb-4 2xl:mb-11'>
            <img
              src={resolvedTheme === 'light' ? logoBlack : logoWhite}
              className='object-contain'
              alt='Humphree logo'
            />
          </div>
          {isHumphreeUser ? (
            <>
              <SidebarOemDropdown />
              {/* Showing a second filtered dropdown if the representingOem is a BC  */}
              {representingOem?.parentOEMTenantId === null && (
                <div className='mt-4'>
                  <SidebarOemDropdownIndirect />
                </div>
              )}
            </>
          ) : (
            <>
              <div className='flex justify-center w-full items-center mb-4'>
                <span className='font-bold text-primary-400'>{currentOem?.name}</span>
              </div>
              {oems.length > 0 && <SidebarOemDropdownIndirect />}
            </>
          )}
          <div className='line w-full mt-4 2xl:mt-10 bg-gray-20'></div>
        </div>

        <div className='w-full flex flex-col flex-1 gap-3 py-7 overflow-auto'>
          {currentOemTenantId === '12345678-1234-1234-1234-123456789000' && (
            <SidebarButton
              active={location.pathname === '/dashboard'}
              icon={<FontAwesomeIcon icon={faDashboard} />}
              title='Dashboard'
              routeName='/dashboard'
              setShowMenu={() => toggleMenuOnlyMobile()}
            />
          )}
          <SidebarButton
            active={
              location.pathname === '/' ||
              location.pathname === '/systems/create' ||
              location.pathname.includes('/systems')
            }
            icon={<FontAwesomeIcon icon={faSitemap} title='Customer systems' />}
            title='Systems'
            routeName='/'
            setShowMenu={() => toggleMenuOnlyMobile()}
          />
          <SidebarButton
            active={location.pathname.includes('/models') || location.pathname.includes('/createModel')}
            icon={<FontAwesomeIcon icon={faShip} title='Boat models' />}
            title='Boat models'
            routeName='models'
            setShowMenu={() => toggleMenuOnlyMobile()}
          />
          <SidebarButton
            active={location.pathname.includes('/orders')}
            icon={<FontAwesomeIcon icon={faShoppingCart} title='Orders' />}
            title='Orders'
            routeName='orders'
            setShowMenu={() => toggleMenuOnlyMobile()}
          />
          {currentOemTenantId === '12345678-1234-1234-1234-123456789000' && (
            <>
              <SidebarButton
                active={location.pathname.includes('/units')}
                icon={<FontAwesomeIcon icon={faGrid} />}
                title='Hardware Units'
                routeName='units'
                setShowMenu={() => toggleMenuOnlyMobile()}
              />
              <SidebarButton
                active={location.pathname.includes('/measurements')}
                icon={<FontAwesomeIcon icon={faMagnifyingGlassChart} />}
                title='Measurements'
                routeName='measurements'
                setShowMenu={() => toggleMenuOnlyMobile()}
              />
              <PermissionGate roles={[ROLES.softwareManager]} rejected={undefined}>
                <SidebarButton
                  active={location.pathname.includes('/software')}
                  icon={<FontAwesomeIcon icon={faCloudArrowDown} />}
                  title='Software update'
                  routeName='software'
                  setShowMenu={() => toggleMenuOnlyMobile()}
                />
              </PermissionGate>
            </>
          )}
        </div>
        <div className='flex flex-col w-full gap-3'>
          <div className='w-max'>
            <ToggleButton
              darkMode
              onClick={() => setTheme(resolvedTheme === 'light' ? 'dark' : 'light')}
              toggled={resolvedTheme !== 'light' ? true : false}
            />
          </div>
          <SidebarButton
            active={location.pathname.includes('/settings')}
            icon={<FontAwesomeIcon icon={faCog} />}
            title='Settings'
            routeName='settings'
            setShowMenu={() => toggleMenuOnlyMobile()}
          ></SidebarButton>
          <a
            href='https://humphree.atlassian.net/wiki/external/NjdlOTc3ZGVkMTYyNGI1Mzk1ZmNhNGFhNzBiYWEwM2Y'
            target='_blank'
            rel='noreferrer'
            className='text-gray-60 2xl:prose-mainMenuL1Default prose-mainMenuL2Default h-8 2xl:px-3 2xl:py-5 group flex flex-row w-full gap-4 justify-between items-center cursor-pointer hover:bg-white'
          >
            <div className='flex flex-row gap-3 items-center whitespace-nowrap cursor-pointer'>
              <FontAwesomeIcon
                className='text-gray-60 cursor-pointer 2xl:prose-mainMenuL1ItemActive prose-mainMenuL2ItemActive'
                icon={faCircleQuestion}
              />
              Help
              <FontAwesomeIcon
                className='text-gray-60 cursor-pointer 2xl:prose-mainMenuL1ItemActive prose-mainMenuL2ItemActive'
                icon={faExternalLink}
              />
            </div>
          </a>
          <div className='flex flex-row items-center justify-between w-full'>
            <div className='flex flex-row gap-4 items-center'>
              <div className='flex flex-col'>
                <span className='prose-mainMenuL2Default text-gray-60'>{account?.name}</span>
              </div>
            </div>
            <div
              ref={ref}
              className='relative cursor-pointer hover:bg-gray-10 px-1 rounded-full'
              onClick={() => setOpenUserEdit(!openUserEdit)}
            >
              <FontAwesomeIcon className='text-gray-60' icon={faEllipsis} />
              {openUserEdit && (
                <>
                  <div className='p-2 absolute -top-28 -right-2 flex-col bg-white shadow-card border border-gray-10 flex items-center'>
                    <div
                      onClick={() => Logout()}
                      className='flex flex-row gap-3 items-center p-2 pr-8 whitespace-nowrap hover:bg-gray-5 w-full cursor-pointer'
                    >
                      <FontAwesomeIcon className='text-gray-40 cursor-pointer text-lg' icon={faLock} />
                      <span className=' prose-paragraphBase text-gray-100 flex-nowrap flex'>Log out</span>
                    </div>
                    <a
                      href='https://humphree.com/tandc-portal/'
                      target='_blank'
                      rel='noreferrer'
                      className='prose-paragraphBase text-gray-100 flex-nowrap flex'
                    >
                      <div className='flex flex-row gap-3 items-center p-2 pr-8 whitespace-nowrap hover:bg-gray-5 w-full cursor-pointer'>
                        <FontAwesomeIcon className='text-gray-40 cursor-pointer text-lg' icon={faFileContract} />
                        About
                        <FontAwesomeIcon className='text-gray-40 cursor-pointer text-lg' icon={faExternalLink} />
                      </div>
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Don't show in prod*/}
          {Config.b2c_domain !== 'humphreecustomers' && (
            <span className='prose-paragraphTiny mt-2 text-gray-60'>{Config.portal_version}</span>
          )}
        </div>
      </div>
    </>
  );
}

export default Sidebar;
